import React, { useState, useEffect } from "react";

// libraries
import { Grid, Dropdown, Form, Label } from "semantic-ui-react";
import Joi from "joi";
import PropTypes from "prop-types";
import { isEqual, get } from "lodash";

// Components
import Input from "@Components/Input";
import TextAreaInput from "@Components/TextArea";
import FileManager from "@Components/FileManager";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";
import IKModal from "@Components/IkModal";

// redux
import { connect } from "react-redux";

// Styles
import styles from "./styles.module.css";

// StaticData
import typesOptions from "./staticData";

const TemplateCrud = ({ modalCrud, tableValue, onClose, data, setData, itemSelect, templateId, metaStatus }) => {
	const [open, setOpen] = useState(false);
	const [modalOption, setModalOption] = useState("");

	const [types, setTypes] = useState("");
	const [text, setText] = useState("");
	const [url, setUrl] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [responseText, setResponseText] = useState("");
	const [responseMode, setResponseMode] = useState("");
	const [responseAttachmentType, setResponseAttachmentType] = useState("");
	const [responseAttachmentUri, setResponseAttachmentUri] = useState("");
	const [initialFormData, setInitialFormData] = useState({});
	const [errors, setErrors] = useState({ types: "", text: "", url: "", phoneNumber: "" });

	const getSchema = () => {
		const defaults = {
			"string.empty": "Este campo es requerido",
			"any.required": "Este campo es requerido",
			"string.min": "Debe tener al menos {#limit} caracteres",
			"string.max": "No debe exceder {#limit} caracteres",
			"number.base": "Debe ser un número válido",
		};

		let schema = Joi.object({
			types: Joi.string()
				.required()
				.messages({
					...defaults,
					"string.empty": "El tipo es requerido",
					"any.required": "El tipo es requerido",
				}),
			text: Joi.string()
				.min(1)
				.required()
				.messages({
					...defaults,
					"string.empty": "El texto del botón es requerido",
					"any.required": "El texto del botón es requerido",
				}),
		});

		if (types === "URL") {
			schema = schema.append({
				url: Joi.string()
					.uri({ scheme: ["http", "https"] })
					.required()
					.messages({
						...defaults,
						"string.empty": "La URL es requerida",
						"any.required": "La URL es requerida",
						"string.uri": "Debe ser una URL válida (http:// o https://)",
					}),
			});
		}

		if (types === "Teléfono") {
			schema = schema.append({
				phoneNumber: Joi.string()
					.min(6)
					.required()
					.messages({
						...defaults,
						"string.empty": "El teléfono es requerido",
						"any.required": "El teléfono es requerido",
						"string.min": "El teléfono debe tener al menos 6 caracteres",
					}),
			});
		}

		return schema;
	};

	useEffect(() => {
		setModalOption(modalCrud);
		if (modalCrud !== "Add") {
			setTypes(get(itemSelect, "type", get(itemSelect, "msgType", "")));
			setText(get(itemSelect, "text", ""));
			setUrl(get(itemSelect, "url", ""));
			setPhoneNumber(get(itemSelect, "phone_number", get(itemSelect, "url", "")));
			setResponseText(get(itemSelect, "responseText", ""));
			setResponseMode(get(itemSelect, "responseText") ? "text" : get(itemSelect, "responseAttachmentUri") ? "attachment" : "");
			setResponseAttachmentType(get(itemSelect, "responseAttachmentType", ""));
			setResponseAttachmentUri(get(itemSelect, "responseAttachmentUri", ""));
			setInitialFormData(itemSelect);
		}
	}, [tableValue]);

	const handlerSaved = async () => {
		const schema = getSchema();
		const validateObj = { types, text };
		if (types === "URL") validateObj.url = url;
		if (types === "Teléfono") validateObj.phoneNumber = phoneNumber;

		const validation = schema.validate(validateObj, { abortEarly: false });
		if (validation.error) {
			const newErrors = { types: "", text: "", url: "", phoneNumber: "" };
			validation.error.details.forEach((err) => {
				if (err.context.key) newErrors[err.context.key] = err.message;
			});
			setErrors(newErrors);
			return;
		}
		setErrors({ types: "", text: "", url: "", phoneNumber: "" });

		const buttonData = {
			msgType: types,
			text,
			...(types === "URL" ? { url } : {}),
			...(types === "Teléfono" ? { phone_number: phoneNumber, url: phoneNumber } : {}),
			...(types === "Copiar Código" ? { text } : {}),
			...(types === "Respuesta Rápida"
				? {
						responseText: responseMode === "text" ? responseText || null : null,
						responseAttachmentType: responseMode === "attachment" ? responseAttachmentType || null : null,
						responseAttachmentUri: responseMode === "attachment" ? responseAttachmentUri || null : null,
				  }
				: {}),
		};

		if (modalOption !== "Add") {
			const dataEdit = {
				...buttonData,
				id: itemSelect.id,
				templateId,
			};
			const newData = data.filter((item) => {
				return item.id !== itemSelect.id;
			});
			setData([...newData, dataEdit]);
			onClose();
		} else {
			const addData = {
				...buttonData,
				templateId,
			};
			setData([...data, addData]);
			onClose();
		}
	};

	const cancelModal = () => {
		onClose();
	};

	const modalOpen = () => {
		const normalize = (val) => {
			return val === undefined || val === null ? "" : val;
		};
		if (
			isEqual(normalize(initialFormData.type || initialFormData.msgType), normalize(types)) &&
			isEqual(normalize(initialFormData.text), normalize(text)) &&
			isEqual(normalize(initialFormData.url), normalize(url)) &&
			isEqual(normalize(initialFormData.phone_number), normalize(phoneNumber))
		) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	return (
		<>
			<IKModal
				title={modalOption === "Add" ? "Agregar Botón" : `Botón - Id: ${String(itemSelect?.id ?? "")}`}
				id={itemSelect?.id}
				mode={modalOption}
				setMode={setModalOption}
				onClose={modalOpen}
				onSuccess={handlerSaved}
				className={styles.modalFirst}
				hideEditButton={modalOption === "Watch" && metaStatus !== "Local" && metaStatus !== "Rechazada"}
				size="tiny">
				<Grid className={styles.fieldset}>
					<Grid.Row columns={1}>
						<Grid.Column>
							<label className={styles.textBoxColor}>
								Tipo: <span className={styles.required}>*</span>
							</label>
							<Dropdown
								value={types}
								options={typesOptions}
								fluid
								disabled={modalOption === "Watch"}
								placeholder="Seleccionar tipo"
								name="types"
								className={styles.inputDropdown}
								clearable
								onChange={(e, { value }) => {
									setTypes(value);
									setErrors({ types: "", text: "", url: "", phoneNumber: "" });
								}}
								search
								selection
							/>
							{errors.types && (
								<Label className={styles.messageError} pointing prompt>
									{errors.types}
								</Label>
							)}
						</Grid.Column>
					</Grid.Row>

					<Grid.Row columns={1}>
						<Grid.Column>
							<TextAreaInput
								label={
									<>
										Texto del botón: <span className={styles.required}>*</span>
									</>
								}
								name="text"
								value={text}
								placeholder={
									types === "Copiar Código"
										? "Ej. Copiar código"
										: types === "URL"
										? "Ej. Ver pedido"
										: types === "Teléfono"
										? "Ej. Llamar ahora"
										: "Ej. Sí, confirmar"
								}
								onChange={(e) => {
									setText(e.target.value);
								}}
								disabled={modalOption === "Watch"}
								minRows={1}
								maxRows={3}
							/>
							{errors.text && (
								<Label className={styles.messageError} pointing prompt>
									{errors.text}
								</Label>
							)}
						</Grid.Column>
					</Grid.Row>

					{types === "URL" && (
						<Grid.Row columns={1}>
							<Grid.Column>
								<Form.Field>
									<label className={styles.textBoxColor}>
										URL: <span className={styles.required}>*</span>
									</label>
									<Input
										value={url}
										placeholder="Ej. https://example.com/pedido/{{1}}"
										className={styles.inputDropdown}
										fluid
										disabled={modalOption === "Watch"}
										onChange={(e, { value }) => {
											setUrl(value);
										}}
									/>
									{errors.url && (
										<Label className={styles.messageError} pointing prompt>
											{errors.url}
										</Label>
									)}
								</Form.Field>
							</Grid.Column>
						</Grid.Row>
					)}

					{types === "Teléfono" && (
						<Grid.Row columns={1}>
							<Grid.Column>
								<Form.Field>
									<label className={styles.textBoxColor}>
										Teléfono: <span className={styles.required}>*</span>
									</label>
									<Input
										value={phoneNumber}
										placeholder="Ej. +5491155551234"
										className={styles.inputDropdown}
										fluid
										disabled={modalOption === "Watch"}
										onChange={(e, { value }) => {
											setPhoneNumber(value);
										}}
									/>
									{errors.phoneNumber && (
										<Label className={styles.messageError} pointing prompt>
											{errors.phoneNumber}
										</Label>
									)}
								</Form.Field>
							</Grid.Column>
						</Grid.Row>
					)}

					{types === "Copiar Código" && (
						<Grid.Row columns={1}>
							<Grid.Column>
								<Form.Field>
									<label className={styles.textBoxColor}>{"El código se copiará del body de la plantilla (variable {{1}})."}</label>
								</Form.Field>
							</Grid.Column>
						</Grid.Row>
					)}

					{types === "Respuesta Rápida" && (
						<>
							<Grid.Row columns={1}>
								<Grid.Column>
									<label className={styles.textBoxColor}>Tipo de respuesta automática:</label>
									<Dropdown
										value={responseMode}
										options={[
											{ key: "none", text: "Sin respuesta", value: "" },
											{ key: "text", text: "Texto", value: "text" },
											{ key: "attachment", text: "Attachment (imagen/documento/video)", value: "attachment" },
										]}
										fluid
										disabled={modalOption === "Watch"}
										placeholder="Seleccionar tipo"
										className={styles.inputDropdown}
										clearable
										onChange={(e, { value }) => {
											setResponseMode(value);
											setResponseText("");
											setResponseAttachmentType("");
											setResponseAttachmentUri(null);
										}}
										selection
									/>
								</Grid.Column>
							</Grid.Row>
							{responseMode === "text" && (
								<Grid.Row columns={1}>
									<Grid.Column>
										<TextAreaInput
											label="Texto de respuesta automática:"
											name="responseText"
											value={responseText}
											placeholder="Ej. Gracias por su interés, le enviaremos más información."
											onChange={(e) => {
												setResponseText(e.target.value);
											}}
											disabled={modalOption === "Watch"}
											minRows={2}
											maxRows={5}
										/>
									</Grid.Column>
								</Grid.Row>
							)}
							{responseMode === "attachment" && (
								<>
									<Grid.Row columns={1}>
										<Grid.Column>
											<Form.Field>
												<label className={styles.textBoxColor}>Tipo de archivo:</label>
												<Dropdown
													value={responseAttachmentType}
													options={[
														{ key: "image", text: "Imagen", value: "Image" },
														{ key: "document", text: "Documento", value: "Document" },
														{ key: "video", text: "Video", value: "Video" },
													]}
													fluid
													disabled={modalOption === "Watch"}
													placeholder="Seleccionar tipo"
													className={styles.inputDropdown}
													clearable
													onChange={(e, { value }) => {
														setResponseAttachmentType(value);
														setResponseAttachmentUri(null);
													}}
													selection
												/>
											</Form.Field>
										</Grid.Column>
									</Grid.Row>
									{responseAttachmentType && (
										<Grid.Row columns={1}>
											<Grid.Column>
												<Form.Field>
													<label className={styles.textBoxColor}>Cargar archivo:</label>
													<FileManager
														images={responseAttachmentUri ? [responseAttachmentUri] : []}
														setImages={(arr) => {
															setResponseAttachmentUri(arr[0] || null);
														}}
														type={responseAttachmentType === "Image" ? "image" : "file"}
														disabled={modalOption === "Watch"}
														width={3}
														height={3}
													/>
												</Form.Field>
											</Grid.Column>
										</Grid.Row>
									)}
								</>
							)}
						</>
					)}
				</Grid>
			</IKModal>

			{open && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Confirmar"
					content="Está seguro que desea salir del formulario"
					onConfirm={cancelModal}
					onDismiss={modalOpen}
				/>
			)}
		</>
	);
};

TemplateCrud.propTypes = {
	tableValue: PropTypes.object,
	onClose: PropTypes.func,
	modalCrud: PropTypes.string,
	data: PropTypes.array,
	setData: PropTypes.func,
	itemSelect: PropTypes.object,
	templateId: PropTypes.number,
	metaStatus: PropTypes.string,
};

function mapStateToProps() {
	return {};
}
export default connect(mapStateToProps)(TemplateCrud);
